import { DateFilter, DateRange, DateTimeFormat, DateTimeText } from "../..";

/**
 * Get a Date object based on a date string in db format. If the string is empty it returns date object of today.
 */
const getDateFromDbFormat = (dbDate: string) => {
  const parts = dbDate.split("-");
  if (parts.length > 2) {
    return new Date(
      parseInt(parts[0]),
      parseInt(parts[1]) - 1,
      parseInt(parts[2]),
    );
  }
  return new Date();
};

/**
 * Get a Date object based on a date+time string in db format ("YYYY-MM-DD HH:mm:ss").
 * If the string is empty or has no date part it returns date object of today.
 */
const getDateTimeFromDbFormat = (dbDateTime: string) => {
  const [datePart, timePart] = dbDateTime.split(" ");
  const date = getDateFromDbFormat(datePart);
  if (timePart) {
    const timeParts = timePart.split(":");
    date.setHours(
      parseInt(timeParts[0]) || 0,
      timeParts.length > 1 ? parseInt(timeParts[1]) || 0 : 0,
      timeParts.length > 2 ? parseInt(timeParts[2]) || 0 : 0,
      0,
    );
  }
  return date;
};

/**
 * Get a Date object (today's date, with the given time set) based on a time string
 * in db format ("HH:mm:ss" or "HH:mm"). If the string is empty it returns date object of today.
 */
const getDateFromDbTimeFormat = (dbTime: string) => {
  const timeParts = dbTime.split(":");
  if (timeParts.length < 2) {
    return new Date();
  }
  const date = new Date();
  date.setHours(
    parseInt(timeParts[0]) || 0,
    parseInt(timeParts[1]) || 0,
    timeParts.length > 2 ? parseInt(timeParts[2]) || 0 : 0,
    0,
  );
  return date;
};

const getDateFromDbFormatFirst = (dbDate: string) => {
  const date = getDateFromDbFormat(dbDate);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDateFromDbFormatLast = (dbDate: string) => {
  const date = getDateFromDbFormat(dbDate);
  date.setHours(23, 59, 59, 999);
  return date;
};

/**
 * Checks if date 1 is before date 2.
 */
const isDateBefore = (date1: string, date2: string) => {
  const d1 = getDateFromDbFormat(date1);
  const d2 = getDateFromDbFormat(date2);
  return d1.getTime() < d2.getTime();
};

const isDateBeforeOrEqual = (date1: string, date2: string) => {
  const d1 = getDateFromDbFormat(date1);
  const d2 = getDateFromDbFormat(date2);
  return d1.getTime() <= d2.getTime();
};

const getDateFilterFromRange = (dateRange: DateRange) => {
  const now = Date.now();
  let diff = 0;
  switch (dateRange) {
    case DateRange.LAST_7_DAYS:
      diff = 6;
      break;
    case DateRange.LAST_30_DAYS:
      diff = 29;
      break;
    case DateRange.LAST_3_MONTHS:
      diff = Math.round(364 / 4);
      break;
    case DateRange.LAST_6_MONTHS:
      diff = Math.round(364 / 2);
      break;
    case DateRange.LAST_YEAR:
      diff = 364;
      break;
    case DateRange.NONE:
      return {
        startDate: undefined,
        endDate: undefined,
      };
    default:
      diff = 0;
      break;
  }
  const startDate = new Date(now - 3600 * 1000 * 24 * diff);
  return {
    startDate: DateTimeText.getDateTimeText(
      startDate,
      DateTimeFormat.BACKEND_DATE,
    ),
    endDate: DateTimeText.getDateTimeText(
      new Date(now),
      DateTimeFormat.BACKEND_DATE,
    ),
  } as DateFilter;
};

/**
 * Get string representation of a date from month and year.
 */
const getDateFromMonthAndYear = (month: number, year: number) => {
  const m = month < 10 ? "0" + month : month;
  return year + "-" + m + "-01";
};

/**
 * Get the month in intuitive number, eg. january = 1, february = 2, etc.
 * @param date
 * @returns
 */
const getMonthFromDate = (date: Date) => {
  return date.getMonth() + 1;
};

const getCurrentYear = () => {
  const now = new Date();
  return now.getFullYear();
};

const getCurrentMonth = () => {
  const now = new Date();
  return now.getMonth() + 1;
};

const getDaysAgo = (date: Date) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diffTime = now.getTime() - d.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getDaysAgoFromDbDate = (dbDate: string) => {
  if (dbDate === "") return null;
  const date = getDateFromDbFormat(dbDate);
  return getDaysAgo(date);
};

export const DateTimeTools = {
  getDateFromDbFormat,
  getDateTimeFromDbFormat,
  getDateFromDbTimeFormat,
  getDateFromDbFormatFirst,
  getDateFromDbFormatLast,
  isDateBefore,
  isDateBeforeOrEqual,
  getDateFilterFromRange,
  getDateFromMonthAndYear,
  getMonthFromDate,
  getCurrentYear,
  getCurrentMonth,
  getDaysAgo,
  getDaysAgoFromDbDate,
};
